// services/apiClient.ts
import { CustomerApi } from "../clients/cds_client/";
import { UsersApi } from "../clients/cds_client/api";
import { handle401Error } from "../oidc-client-auth/handle401Error";
import { getAuthedAxios } from "./config/authedAxios";
import { createOpenApiClientFactory } from "./config/openApiClientFactory";
import { PartiesV2Api, PointOfContactV2Api } from "../clients/cds_client/api";

// Ensure environment variables are set
const BASE_URL = process.env.NEXT_PUBLIC_API_URL;

if (!BASE_URL) {
  throw new Error("Missing API environment variables. Check .env file.");
}

const axiosInstance = getAuthedAxios(BASE_URL);
const createCdsApi = createOpenApiClientFactory(BASE_URL, axiosInstance);

// Create API client instances
const _customerApi = createCdsApi(CustomerApi);
const _userApi = createCdsApi(UsersApi);
const _partiesApi = createCdsApi(PartiesV2Api);
const _pointOfContactApi = createCdsApi(PointOfContactV2Api);
// Create wrapper functions to handle 401 errors
const wrapApiMethod = (apiMethod: Function, redirectToSigninOnError?: boolean) => {
  return async (...args: any[]) => {
    try {
      return await apiMethod(...args);
    } catch (error: any) {
      const status = error?.response?.status ?? error?.status;
      if (redirectToSigninOnError || status === 401) {
        const currentPath =
          typeof window !== "undefined"
            ? `${window.location.pathname}${window.location.search}`
            : "/";
        handle401Error(currentPath);
      }

      // Always rethrow so callers can handle/report the original error.
      throw error;
    }
  };
};

// Create proxies for each API to intercept calls and handle 401 errors
export const customerApi = new Proxy(_customerApi, {
  get(target, prop) {
    const originalMethod = target[prop as keyof typeof target];
    if (typeof originalMethod === 'function') {
      return wrapApiMethod(originalMethod.bind(target));
    }
    return originalMethod;
  }
});

export const userApi = new Proxy(_userApi, {
  get(target, prop) {
    const originalMethod = target[prop as keyof typeof target];
    if (typeof originalMethod === 'function') {
      const redirectToSigninOnError = true;
      return wrapApiMethod(originalMethod.bind(target), redirectToSigninOnError);
    }
    return originalMethod;
  }
});


export const partiesApi = new Proxy(_partiesApi, {
  get(target, prop) {
    const originalMethod = target[prop as keyof typeof target];
    if (typeof originalMethod === 'function') {
      return wrapApiMethod(originalMethod.bind(target));
    }
    return originalMethod;
  }
});

export const pointOfContactApi = new Proxy(_pointOfContactApi, {
  get(target, prop) {
    const originalMethod = target[prop as keyof typeof target];
    if (typeof originalMethod === "function") {
      return wrapApiMethod(originalMethod.bind(target));
    }
    return originalMethod;
  },
});